import { Eye, Edit05, Settings01 } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';

export type DocumentViewMode = 'view' | 'review' | 'manager';

interface DocumentModeSelectorProps {
  currentMode: DocumentViewMode;
  onModeChange: (mode: DocumentViewMode) => void;
  availableModes?: DocumentViewMode[];
  disabled?: boolean;
}

const MODE_CONFIG: Record<DocumentViewMode, {
  icon: typeof Eye;
  labelKey: string;
  defaultLabel: string;
  description: string;
}> = {
  view: {
    icon: Eye,
    labelKey: 'modes.view',
    defaultLabel: 'View',
    description: 'View document and extracted data',
  },
  review: {
    icon: Edit05,
    labelKey: 'modes.review',
    defaultLabel: 'Review',
    description: 'Annotate, discuss, and resolve issues',
  },
  manager: {
    icon: Settings01,
    labelKey: 'modes.manager',
    defaultLabel: 'Manager',
    description: 'Versions, classification, and file actions',
  },
};

export function DocumentModeSelector({
  currentMode,
  onModeChange,
  availableModes = ['view', 'review', 'manager'],
  disabled = false,
}: DocumentModeSelectorProps) {
  const { t } = useTranslation(['pages:documents']);

  return (
    <div className="flex items-center bg-secondary rounded-lg p-1 gap-1">
      {availableModes.map((mode) => {
        const config = MODE_CONFIG[mode];
        const Icon = config.icon;
        const isActive = currentMode === mode;

        return (
          <button
            key={`mode-tab-${mode}`}
            type="button"
            onClick={() => !disabled && onModeChange(mode)}
            disabled={disabled}
            className={`
              flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
              ${isActive
                ? 'bg-primary text-primary shadow-sm'
                : 'text-tertiary hover:text-primary hover:bg-primary/50'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={config.description}
          >
            <Icon className="size-4" />
            <span>{t(config.labelKey, { defaultValue: config.defaultLabel })}</span>
          </button>
        );
      })}
    </div>
  );
}

export default DocumentModeSelector;
